/*
 * Copyright 2026 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/checkin_alert.svg";
import __asset_1 from "@res/icons/checkin_complete.svg";
import __asset_2 from "@res/icons/connectivity.svg";
import __asset_3 from "@res/icons/connectivity_off.svg";
import __asset_4 from "@res/icons/dots_vertical.svg";
import __asset_5 from "@res/icons/everyone_checked_in.svg";
import * as React from "react";
import dayjs from "../utils/dayjs";
import {BaseComponent, Dialog_Builder} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_Patient} from "@app/app-shared/patient";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {OrganizationsContext} from "./ui/organizations/OrganizationsContext";
import {PatientsContext} from "./ui/patients/PatientsContext";
import {CheckinModule, OnCheckinUpdated} from "@modules/CheckinModule";
import {UpdateCheckInDialog} from "./ui/checkin/UpdateCheckInDialog";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {padding_rl_lg, padding_tb_lg, Spacing} from "@styles/styles";
import * as emotion from "emotion";
import {Loader} from "../../widgets/Loader";

const checkin_alert = __asset_0;
const checkin_complete = __asset_1;
const connectivity_on = __asset_2;
const connectivity_off = __asset_3;
const dots_vertical = __asset_4;
const everyone_checked_in = __asset_5;

const ASSISTED_LIVING_NAME_TOKEN = "masonicare";

const cardStyle = emotion.css({
    backgroundColor: COLORS.white,
    borderRadius: 8,
    boxShadow: "0 1px 8px 0 rgba(0,0,0,0.06)",
    flex: 1,
    minWidth: 0,
    overflow: "auto"
});

const allDoneStyle = emotion.css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
});

const formatLastInteraction = (ts?: number, tz?: string) => {
    if (!ts) return "n/a";
    const m = tz ? dayjs.tz(ts, tz) : dayjs(ts);
    const isToday = m.isSame(dayjs(), "day");
    return isToday ? `Today, ${m.format("h:mm A")}` : m.format("MMM D, h:mm A");
};

export const Page_CheckIn = () => (
    <OrganizationsContext.Consumer>{
        ({organizations}) => (
            <PatientsContext.Consumer>{
                ({patients}) => {
                    const targetOrgs = Object.values(organizations)
                        .filter((o): o is DB_Organization => !!o && (o.name?.toLowerCase().includes(ASSISTED_LIVING_NAME_TOKEN) ?? false));
                    const targetOrgIds = new Set(targetOrgs.map(o => o._id));
                    // Match the eligibility used by the daily check-in cron so the
                    // dashboard count and the alert count agree: active patients with
                    // a known timezone. Patients without a timezone can't be reasoned
                    // about against the 05:00 local window.
                    const targetPatients = Object.values(patients)
                        .filter((p): p is DB_Patient =>
                            !!p && targetOrgIds.has(p.organizationId) && !p.disabled && !!p.timezone);
                    return <CheckInDashboard patients={targetPatients}/>;
                }
            }</PatientsContext.Consumer>
        )
    }</OrganizationsContext.Consumer>
);

type DashProps = { patients: DB_Patient[] };

// Device pushes runtime status every ~10 min and morning-activity is read in
// the same call. Anything tighter than the device cadence is wasted load.
const POLL_INTERVAL_MS = 10 * 60 * 1000;

class CheckInDashboard extends BaseComponent<DashProps, {}> implements OnCheckinUpdated {
    __onCheckinUpdated = () => this.forceUpdate();

    private pollHandle?: number;

    componentDidMount() {
        this.fetchActivity();
        this.pollHandle = window.setInterval(this.fetchActivity, POLL_INTERVAL_MS);
    }

    componentWillUnmount() {
        if (this.pollHandle !== undefined) {
            window.clearInterval(this.pollHandle);
            this.pollHandle = undefined;
        }
    }

    componentDidUpdate(prev: DashProps) {
        const prevIds = prev.patients.map(p => p.unitId).join();
        const nextIds = this.props.patients.map(p => p.unitId).join();
        if (prevIds !== nextIds) this.fetchActivity();
    }

    private fetchActivity = () => {
        const unitIds = this.props.patients.map(p => p.unitId);
        if (unitIds.length) CheckinModule.fetchMany(unitIds);
    };

    private openUpdateDialog = (patient: DB_Patient) => {
        new Dialog_Builder(<UpdateCheckInDialog patient={patient}/>)
            .addStyle({backgroundColor: "unset", boxShadow: "unset"})
            .setZIndex(100)
            .show();
    };

    private renderRow = (patient: DB_Patient, isCheckedIn: boolean) => {
        const activity = CheckinModule.get(patient.unitId);
        const latest = activity ? Math.max(activity.lastInteraction || 0, activity.lastInteractionOverride || 0) : 0;
        const statusIcon = isCheckedIn ? checkin_complete : checkin_alert;
        const isOnline = CheckinModule.isOnline(patient.unitId);
        return (
            <div key={patient._id}
                 className={"ll_h_c"}
                 style={{
                     padding: `${Spacing.s}px ${Spacing.m}px`,
                     borderBottom: `1px solid ${COLORS.color_gray_medium}`,
                     gap: Spacing.m
                 }}>
                <div style={{width: 24}}><img alt={isCheckedIn ? "checked-in" : "missed"} src={statusIcon} height={24}/></div>
                <div style={{flex: 2}}>{FONTS.bold_16(patient.name)}</div>
                <div style={{flex: 2}}>{FONTS.reg_16_black(formatLastInteraction(latest, patient.timezone))}</div>
                <div style={{flex: 1}}>
                    {/* Connectivity is driven by CheckinActivityItem.lastConnected,
                        piggybacked on the morning-activity poll. Falls back to
                        the off icon while lastConnected is undefined (q-server
                        may not yet be returning it on every deploy). */}
                    <img alt={isOnline ? "online" : "offline"}
                         src={isOnline ? connectivity_on : connectivity_off}
                         height={20}/>
                </div>
                <div className={"clickable"}
                     style={{width: 24, textAlign: "center"}}
                     onClick={() => this.openUpdateDialog(patient)}>
                    <img alt={"more"} src={dots_vertical} height={20}/>
                </div>
            </div>
        );
    };

    private renderColumn = (title: string, count: number, patients: DB_Patient[], isCheckedIn: boolean) => (
        <div className={cardStyle}>
            <div style={{padding: Spacing.m, borderBottom: `1px solid ${COLORS.color_gray_medium}`}}>
                <div className={"ll_h_c"} style={{gap: Spacing.s}}>
                    {FONTS.medium_21(`${count}`)}
                    {FONTS.reg_16_black(title)}
                </div>
            </div>
            {patients.length === 0
                ? <div style={{padding: Spacing.l}}>{FONTS.reg_16_black("None")}</div>
                : patients.map(p => this.renderRow(p, isCheckedIn))}
        </div>
    );

    private renderAllDone = () => (
        <div className={allDoneStyle}>
            <img alt={"everyone checked in"} src={everyone_checked_in} width={174} height={110}/>
            <div style={{
                marginTop: Spacing.l,
                fontSize: 22,
                color: COLORS.neutral_text_dark,
                lineHeight: 1.4
            }}>
                <div>Everyone checked in</div>
                <div>Have a great day!</div>
            </div>
        </div>
    );

    render() {
        const unitIds = this.props.patients.map(p => p.unitId);
        // Show a loader until activity data is in for every patient — avoids
        // the flicker where everyone briefly looks "missed" before fetch resolves.
        // position: relative scopes the spinner's absolute fill to this wrapper
        // (otherwise it climbs to the .full_screen root and covers header/sidebar).
        if (unitIds.length && !CheckinModule.hasDataFor(unitIds)) {
            return (
                <div className={`${padding_rl_lg} ${padding_tb_lg}`}
                     style={{flex: 1, position: "relative"}}>
                    <Loader/>
                </div>
            );
        }

        const missed: DB_Patient[] = [];
        const checkedIn: DB_Patient[] = [];
        this.props.patients.forEach(p => {
            (CheckinModule.isCheckedInToday(p.unitId, p.timezone) ? checkedIn : missed).push(p);
        });

        const allDone = this.props.patients.length > 0 && missed.length === 0;

        return (
            <div className={`ll_v_s ${padding_rl_lg} ${padding_tb_lg}`} style={{flex: 1, gap: Spacing.l, overflow: "hidden"}}>
                <div className={"ll_h_c"} style={{gap: Spacing.l, flex: 1, alignItems: "stretch", overflow: "hidden"}}>
                    {allDone
                        ? this.renderAllDone()
                        : this.renderColumn("haven't checked in", missed.length, missed, false)}
                    {this.renderColumn("checked in", checkedIn.length, checkedIn, true)}
                </div>
            </div>
        );
    }
}
